import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import {
    Card,
    DatePicker,
    Button,
    Space,
    Typography,
    Spin,
    Empty,
    Tag,
    Tooltip,
    Collapse,
    App,
    Alert,
    message,
} from "antd";
import {
    RefreshCw,
    TrendingUp,
    Wallet,
    ArrowUpRight,
    ArrowDownRight,
    User,
    LayoutDashboard,
    Info,
    BarChart3,
    Activity,
    FileText,
    Shield,
    Briefcase,
    Tags,
} from "lucide-react";
import { RiseOutlined, FallOutlined, SwapOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import apiService from "../../services/api";
import useTheme from "../../hooks/useTheme";
import {
    buildAccountPairGroups,
    buildPairSplitGroups,
    buildExchangeVenueVolumeGroups,
} from "../../utils/tradeTrendChartData";
import { formatExchangeName, formatChartTooltipValue } from "../../utils/formatters";
import HedgePanoramaSection from "../HedgeCharts/HedgePanoramaSection";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { withOwnerUserId } from "../../utils/ownerScopeParams";
import { useAdminPanoramaUsers } from "../../hooks/useAdminPanoramaUsers";
import PanoramaExportButtons from "../common/PanoramaExportButtons";
import {
    resolvePanoramaExportOwnerUserId,
    exportCompositeOverviewExcel,
} from "../../utils/panoramaExportExcel";
import { capturePanoramaToPdf } from "../../utils/panoramaPdfCapture";
import "./CompositeOverview.css";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ChartTitle,
    ChartTooltip,
    Legend,
    Filler,
    ChartDataLabels
);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// 近 N 天、本月均不含「今天」，截止昨天
const QUICK_DATES = [
    { label: "今天", value: () => [dayjs(), dayjs()] },
    {
        label: "近1天",
        value: () => {
            const y = dayjs().subtract(1, "day");
            return [y.startOf("day"), y.endOf("day")];
        },
    },
    {
        label: "近2天",
        value: () => [dayjs().subtract(2, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "近3天",
        value: () => [dayjs().subtract(3, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "近7天",
        value: () => [dayjs().subtract(7, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "近30天",
        value: () => [dayjs().subtract(30, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "本月",
        value: () => {
            const end = dayjs().subtract(1, "day").endOf("day");
            const start = dayjs().startOf("month").startOf("day");
            if (start.isAfter(end)) {
                return [end.startOf("day"), end];
            }
            return [start, end];
        },
    },
];

const COLORS = {
    primary: "#1890ff",
    success: "#52c41a",
    warning: "#faad14",
    error: "#ff4d4f",
    purple: "#722ed1",
    series: [
        { primary: "#1890ff", secondary: "rgba(24, 144, 255, 0.15)" },
        { primary: "#52c41a", secondary: "rgba(82, 196, 26, 0.15)" },
        { primary: "#faad14", secondary: "rgba(250, 173, 20, 0.15)" },
        { primary: "#f5222d", secondary: "rgba(245, 34, 45, 0.15)" },
        { primary: "#722ed1", secondary: "rgba(114, 46, 209, 0.15)" },
    ],
};

/** 单份看板内容（全平台 ownerUserId=null，或某用户 ID） */
function CompositeOverviewBody({ ownerUserId = null, dateRange }) {
    const { isDark } = useTheme();
    const { message: msg } = App.useApp();
    const [loading, setLoading] = useState(false);

    // Data states
    const [equityTotal, setEquityTotal] = useState([]);
    const [equityAccounts, setEquityAccounts] = useState([]);
    const [tradeSummary, setTradeSummary] = useState(null);
    const [tradeDaily, setTradeDaily] = useState({ datasets: [] });
    const [tradeDailyMeta, setTradeDailyMeta] = useState({ totalDays: 0, activeDays: 0, zeroDays: 0 });
    const [tradeAccountPairGroups, setTradeAccountPairGroups] = useState([]);
    const [tradePairSplitGroups, setTradePairSplitGroups] = useState([]);
    const [tradeExchangeVenueGroups, setTradeExchangeVenueGroups] = useState([]);
    const [equityExchangeRaw, setEquityExchangeRaw] = useState([]);
    const [equityBusinessLines, setEquityBusinessLines] = useState([]);
    const [equityStrategyTypes, setEquityStrategyTypes] = useState([]);

    const [canViewHedge] = useState(() => {
        try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            return hasPermission(u, PERMISSIONS.VIEW_HEDGE_GROUPS);
        } catch {
            return false;
        }
    });

    const formatVolume = useCallback((val) => {
        if (!val && val !== 0) return "0";
        if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
        if (val >= 1000) return (val / 1000).toFixed(1) + "K";
        return val.toFixed(2);
    }, []);

    const hasTradeActivity = useCallback((item) => {
        if (!item) return false;
        return (
            Number(item.total_records || 0) > 0 ||
            parseFloat(item.total_volume || 0) > 0 ||
            parseFloat(item.maker_volume || 0) > 0 ||
            parseFloat(item.taker_volume || 0) > 0
        );
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = withOwnerUserId(
                {
                    date_from: dateRange[0].format("YYYY-MM-DD"),
                    date_to: dateRange[1].format("YYYY-MM-DD"),
                },
                ownerUserId,
            );

            const [
                totalEqRes,
                accEqRes,
                exchangeEqRes,
                blEqRes,
                stEqRes,
                tradeStatsRes,
                tradeDailyRes,
                tradeAccPairRes,
                tradePairTrendRes,
                tradeExchangeDailyRes,
            ] = await Promise.all([
                apiService.equity.getTotalTrends(params),
                apiService.equity.getAccountTrends(params),
                apiService.equity.getExchangeTrends(params),
                apiService.equity.getBusinessLineTrends(params),
                apiService.equity.getStrategyTypeTrends(params),
                apiService.trades.getTradeStats(params),
                apiService.trades.getDailyStats({ ...params, limit: 100 }),
                apiService.trades.getAccountPairTrendData(params),
                apiService.trades.getPairTrendData(params),
                apiService.trades.getExchangeDailyTrendData(params),
            ]);

            if (totalEqRes.data?.success) setEquityTotal(totalEqRes.data.data || []);
            if (accEqRes.data?.success) setEquityAccounts(accEqRes.data.data || []);
            if (exchangeEqRes.data?.success) setEquityExchangeRaw(exchangeEqRes.data.data || []);
            else setEquityExchangeRaw([]);
            if (blEqRes.data?.success) setEquityBusinessLines(blEqRes.data.data || []);
            else setEquityBusinessLines([]);
            if (stEqRes.data?.success) setEquityStrategyTypes(stEqRes.data.data || []);
            else setEquityStrategyTypes([]);
            if (tradeStatsRes.data?.success) setTradeSummary(tradeStatsRes.data.data);

            // Process Trade Daily
            const dailyRaw = tradeDailyRes.data.data || [];
            const rDailyRaw = [...dailyRaw].reverse();
            const labels = rDailyRaw.map(item => dayjs(item.date).format("MM-DD"));
            const totalVolumes = rDailyRaw.map(item => parseFloat(item.total_volume || 0));
            const makerVolumes = rDailyRaw.map(item => parseFloat(item.maker_volume || 0));
            const activeDays = dailyRaw.filter(hasTradeActivity).length;
            
            setTradeDaily({
                labels,
                datasets: [
                    {
                        label: "总交易量",
                        data: totalVolumes,
                        borderColor: COLORS.primary,
                        backgroundColor: "rgba(24, 144, 255, 0.1)",
                        fill: true,
                        tension: 0.4,
                        pointRadius: 4,
                    },
                    {
                        label: "Maker量",
                        data: makerVolumes,
                        borderColor: COLORS.success,
                        backgroundColor: "rgba(82, 196, 26, 0.1)",
                        fill: true,
                        tension: 0.4,
                        pointRadius: 4,
                    }
                ]
            });
            setTradeDailyMeta({
                totalDays: dailyRaw.length,
                activeDays,
                zeroDays: Math.max(dailyRaw.length - activeDays, 0),
            });

            setTradeAccountPairGroups(buildAccountPairGroups(tradeAccPairRes.data.data || []));
            setTradePairSplitGroups(buildPairSplitGroups(tradePairTrendRes.data.data || []));
            if (tradeExchangeDailyRes.data?.success) {
                setTradeExchangeVenueGroups(
                    buildExchangeVenueVolumeGroups(tradeExchangeDailyRes.data.data || []),
                );
            } else {
                setTradeExchangeVenueGroups([]);
            }

        } catch (err) {
            console.error("Fetch data error:", err);
            msg.error("加载数据失败");
        } finally {
            setLoading(false);
        }
    }, [dateRange, ownerUserId, msg, hasTradeActivity]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ECharts Option for Equity
    const getEquityOption = (dates, netAssets, pnls) => {
        const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
        return {
            backgroundColor: "transparent",
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                formatter: (params) => {
                    if (!params?.length) return "";
                    let html = `<div style="margin-bottom:4px;font-weight:600">${params[0].axisValue}</div>`;
                    params.forEach((p) => {
                        html += `<div style="display:flex;justify-content:space-between;gap:20px;"><span>${p.marker} ${p.seriesName}</span><b>${formatChartTooltipValue(p.value)}</b></div>`;
                    });
                    return html;
                },
            },
            legend: { bottom: 0, data: ["净资产", "当日盈亏"], textStyle: { color: textColor } },
            grid: { left: "3%", right: "4%", bottom: "15%", top: "15%", containLabel: true },
            xAxis: { type: "category", data: dates, axisLabel: { color: textColor } },
            yAxis: [
                { type: "value", name: "净资产", nameTextStyle: { color: textColor }, scale: true, axisLabel: { color: textColor } },
                { type: "value", name: "盈亏", nameTextStyle: { color: textColor }, scale: true, axisLabel: { color: textColor }, splitLine: { show: false } }
            ],
            series: [
                {
                    name: "净资产",
                    type: "line",
                    data: netAssets,
                    smooth: true,
                    lineStyle: { width: 3, color: "#1890ff" },
                    itemStyle: { color: "#1890ff" },
                    areaStyle: { color: 'rgba(24,144,255,0.1)' },
                    label: {
                        show: true,
                        position: 'top',
                        formatter: (params) => formatVolume(params.value),
                        color: textColor,
                        fontSize: 10,
                        distance: 10,
                        padding: [4, 4],
                        backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.6)',
                        borderRadius: 4
                    }
                },
                {
                    name: "当日盈亏",
                    type: "line",
                    yAxisIndex: 1,
                    smooth: true,
                    data: pnls,
                    lineStyle: { width: 2.5, color: "#fa8c16" },
                    symbol: "circle",
                    itemStyle: { color: "#fa8c16" },
                    label: {
                        show: true,
                        position: "bottom",
                        formatter: (params) => (params.value !== 0 && params.value != null ? params.value.toFixed(0) : ""),
                        color: textColor,
                        fontSize: 10,
                        distance: 8
                    }
                }
            ]
        };
    };

    // Chart.js Option for Volume
    const volumeOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        layout: {
            padding: { top: 30, left: 10, right: 10, bottom: 10 }
        },
        plugins: {
            legend: { display: true, position: 'top', align: 'end' },
            tooltip: {
                enabled: true,
                backgroundColor: isDark ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                titleColor: isDark ? '#fff' : '#1e293b',
                bodyColor: isDark ? '#cbd5e1' : '#475569',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                borderWidth: 1,
                padding: 10,
                displayColors: true,
                callbacks: {
                    label: (context) => {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';
                        if (context.parsed.y !== null) {
                            label += formatChartTooltipValue(context.parsed.y) + ' USDT';
                        }
                        return label;
                    }
                }
            },
            datalabels: {
                display: true,
                align: 'top',
                anchor: 'end',
                offset: 5,
                formatter: (v) => formatVolume(v),
                font: { size: 10, weight: 'bold' },
                color: isDark ? "rgba(255,255,255,0.6)" : "#64748b"
            }
        },
        scales: {
            x: { 
                grid: { display: false }, 
                ticks: { color: isDark ? "rgba(255,255,255,0.45)" : "#64748b" } 
            },
            y: { 
                beginAtZero: true, 
                grid: { color: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" },
                ticks: { 
                    color: isDark ? "rgba(255,255,255,0.45)" : "#64748b", 
                    callback: (v) => formatVolume(v) 
                } 
            }
        }
    }), [isDark, formatVolume, formatChartTooltipValue]);

    /** 账号分交易对多线（仅成交量） */
    const accountPairMultiLineOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: { display: true, position: "top", labels: { boxWidth: 8, font: { size: 9 } } },
            datalabels: { display: false },
            tooltip: {
                enabled: true,
                backgroundColor: isDark ? "rgba(30, 41, 59, 0.9)" : "rgba(255, 255, 255, 0.9)",
                titleColor: isDark ? "#fff" : "#1e293b",
                bodyColor: isDark ? "#cbd5e1" : "#475569",
                borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                borderWidth: 1,
                padding: 8,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${formatChartTooltipValue(ctx.parsed.y)} USDT`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: isDark ? "rgba(255,255,255,0.45)" : "#64748b", font: { size: 9 } },
            },
            y: {
                beginAtZero: true,
                grid: { color: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" },
                ticks: {
                    color: isDark ? "rgba(255,255,255,0.45)" : "#64748b",
                    callback: (v) => formatVolume(v),
                },
            },
        },
    }), [isDark, formatVolume, formatChartTooltipValue]);

    /** 单交易对现货/合约：成交量 + Maker 双轴 */
    const pairDualAxisOptions = useMemo(() => ({
        ...volumeOptions,
        plugins: {
            ...volumeOptions.plugins,
            legend: { display: false },
            datalabels: { display: false },
        },
        scales: {
            x: volumeOptions.scales.x,
            y: {
                ...volumeOptions.scales.y,
                position: "left",
            },
            y1: {
                type: "linear",
                display: true,
                position: "right",
                beginAtZero: true,
                grid: { drawOnChartArea: false },
                ticks: {
                    color: COLORS.success,
                    callback: (v) => formatVolume(v),
                },
            },
        },
    }), [volumeOptions]);

    const translateTradeType = useCallback((type) => {
        if (!type) return "";
        const lower = type.toLowerCase();
        if (lower === "spot") return "现货";
        if (lower === "swap" || lower === "futures") return "合约";
        return type;
    }, []);

    /** 交易所资金：每条为交易所+交易类型一行图 */
    const equityExchangeChartRows = useMemo(() => {
        const list = equityExchangeRaw || [];
        const rows = list
            .filter((g) => g.trends && g.trends.length > 0)
            .map((g) => ({
                key: `${g.exchange}|${g.trade_type}`,
                exchange: g.exchange,
                tradeType: g.trade_type,
                trends: g.trends,
            }));
        rows.sort((a, b) => {
            const c = String(a.exchange).localeCompare(String(b.exchange));
            if (c !== 0) return c;
            return String(a.tradeType).localeCompare(String(b.tradeType));
        });
        return rows;
    }, [equityExchangeRaw]);

    // Stats
    const latestNetAssets = equityTotal.length > 0 ? (equityTotal[equityTotal.length - 1]?.total_net_assets || 0) : 0;
    const totalPeriodPnl = equityTotal.reduce((sum, t) => sum + (t.total_pnl || 0), 0);

    return (
        <div className="composite-overview-body">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                <Space size="small" wrap>
                    {ownerUserId != null && (
                        <Tag color="blue">用户数据 · ID {ownerUserId}</Tag>
                    )}
                </Space>
                <Button type="primary" icon={<RefreshCw size={14} />} onClick={fetchData} loading={loading}>
                    刷新本面板
                </Button>
            </div>

            <div className="stats-grid-6">
                <Card className="stat-card blue-glass" variant="borderless">
                    <div className="stat-top">
                        <div className="stat-icon-wrapper"><Wallet size={18} color="white" /></div>
                        <div className="stat-label">总净资产</div>
                    </div>
                    <div className="stat-value">{latestNetAssets.toLocaleString(undefined, { minimumFractionDigits: 2 })} <small>USDT</small></div>
                </Card>
                <Card className={`stat-card ${totalPeriodPnl >= 0 ? 'green-glass' : 'red-glass'}`} variant="borderless">
                    <div className="stat-top">
                        <div className="stat-icon-wrapper"><TrendingUp size={18} color="white" /></div>
                        <div className="stat-label">周期盈亏</div>
                    </div>
                    <div className="stat-value">
                        {totalPeriodPnl >= 0 ? <ArrowUpRight size={18} color="white" /> : <ArrowDownRight size={18} color="white" />}
                        {totalPeriodPnl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                </Card>
                <Card className="stat-card purple-glass" variant="borderless">
                    <div className="stat-top">
                        <div className="stat-icon-wrapper"><BarChart3 size={18} color="white" /></div>
                        <div className="stat-label">总交易量</div>
                    </div>
                    <div className="stat-value">{formatVolume(tradeSummary?.total_volume || 0)} <small>USDT</small></div>
                </Card>
                <Card className="stat-card cyan-glass" variant="borderless">
                    <div className="stat-top">
                        <div className="stat-icon-wrapper"><FileText size={18} color="white" /></div>
                        <div className="stat-label">记录数</div>
                    </div>
                    <div className="stat-value">{(tradeSummary?.total_records || 0).toLocaleString()}</div>
                </Card>
                <Card className="stat-card orange-glass" variant="borderless">
                    <div className="stat-top">
                        <div className="stat-icon-wrapper"><Activity size={18} color="white" /></div>
                        <div className="stat-label">活跃账号</div>
                    </div>
                    <div className="stat-value">{tradeAccountPairGroups.length}</div>
                </Card>
                <Card className="stat-card indigo-glass" variant="borderless">
                    <div className="stat-top">
                        <div className="stat-icon-wrapper"><SwapOutlined style={{ color: 'white', fontSize: 16 }} /></div>
                        <div className="stat-label">活跃对</div>
                    </div>
                    <div className="stat-value">{tradePairSplitGroups.length}</div>
                </Card>
            </div>

            <Spin spinning={loading}>
                <Collapse ghost defaultActiveKey={canViewHedge ? ['equity', 'hedge', 'volume'] : ['equity', 'volume']} className="main-collapse" items={[
                    {
                        key: 'equity',
                        label: (
                            <span>
                                <TrendingUp size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                <strong>资金盈亏走势</strong>
                            </span>
                        ),
                        children: (
                            <div className="charts-section">
                                <Card className="main-chart-card">
                                    <ReactECharts option={getEquityOption(equityTotal.map(t => dayjs(t.date).format("MM-DD")), equityTotal.map(t => t.total_net_assets), equityTotal.map(t => t.total_pnl))} style={{ height: "400px" }} />
                                </Card>
                                <Collapse ghost className="sub-collapse" items={[
                                    {
                                        key: 'sub-eq',
                                        label: '分账号走势图',
                                        children: (
                                            <div className="sub-grid">
                                                {equityAccounts.map(acc => (
                                                    <Card key={`${acc.account}-${acc.exchange}-${acc.trade_type}`} title={`${acc.account} (${formatExchangeName(acc.exchange)}, ${translateTradeType(acc.trade_type)})`} size="small" className="sub-chart-card">
                                                        <ReactECharts option={getEquityOption(acc.trends.map(t => dayjs(t.equity_date).format("MM-DD")), acc.trends.map(t => t.net_assets), acc.trends.map(t => t.pnl))} style={{ height: "240px" }} />
                                                    </Card>
                                                ))}
                                            </div>
                                        )
                                    },
                                    {
                                        key: "sub-eq-bl",
                                        label: (
                                            <span>
                                                <Briefcase size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
                                                分业务线走势 ({equityBusinessLines.length})
                                            </span>
                                        ),
                                        children: (
                                            <div className="sub-grid">
                                                {equityBusinessLines.map((row) => {
                                                    const title = row.code
                                                        ? `${row.name}（${row.code}）`
                                                        : row.name || (row.id === 0 ? "未分配" : `ID ${row.id}`);
                                                    return (
                                                        <Card key={`eq-bl-${row.id}`} title={title} size="small" className="sub-chart-card">
                                                            {(row.trends || []).length > 0 ? (
                                                                <ReactECharts
                                                                    option={getEquityOption(
                                                                        row.trends.map((t) => dayjs(t.equity_date).format("MM-DD")),
                                                                        row.trends.map((t) => t.net_assets),
                                                                        row.trends.map((t) => t.pnl),
                                                                    )}
                                                                    style={{ height: "240px" }}
                                                                />
                                                            ) : (
                                                                <Empty description="无数据" />
                                                            )}
                                                        </Card>
                                                    );
                                                })}
                                                {equityBusinessLines.length === 0 && (
                                                    <Empty description="暂无业务线汇总" style={{ margin: 24 }} />
                                                )}
                                            </div>
                                        ),
                                    },
                                    {
                                        key: "sub-eq-st",
                                        label: (
                                            <span>
                                                <Tags size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
                                                分策略类型走势 ({equityStrategyTypes.length})
                                            </span>
                                        ),
                                        children: (
                                            <div className="sub-grid">
                                                {equityStrategyTypes.map((row) => {
                                                    const title = row.code
                                                        ? `${row.name}（${row.code}）`
                                                        : row.name || (row.id === 0 ? "未分配" : `ID ${row.id}`);
                                                    return (
                                                        <Card key={`eq-st-${row.id}`} title={title} size="small" className="sub-chart-card">
                                                            {(row.trends || []).length > 0 ? (
                                                                <ReactECharts
                                                                    option={getEquityOption(
                                                                        row.trends.map((t) => dayjs(t.equity_date).format("MM-DD")),
                                                                        row.trends.map((t) => t.net_assets),
                                                                        row.trends.map((t) => t.pnl),
                                                                    )}
                                                                    style={{ height: "240px" }}
                                                                />
                                                            ) : (
                                                                <Empty description="无数据" />
                                                            )}
                                                        </Card>
                                                    );
                                                })}
                                                {equityStrategyTypes.length === 0 && (
                                                    <Empty description="暂无策略类型汇总" style={{ margin: 24 }} />
                                                )}
                                            </div>
                                        ),
                                    },
                                    {
                                        key: 'sub-eq-exchange',
                                        label: `各交易所资金走势 (${equityExchangeChartRows.length})`,
                                        children: equityExchangeChartRows.length > 0 ? (
                                            <div className="exchange-venue-stack">
                                                {equityExchangeChartRows.map((row) => (
                                                    <div key={row.key} className="exchange-venue-chart-row">
                                                        <div className="exchange-venue-row-head">
                                                            <div className="exchange-venue-row-head-main">
                                                                <span className="exchange-venue-row-title">{formatExchangeName(row.exchange)}</span>
                                                                <span className="exchange-venue-row-kind">{translateTradeType(row.tradeType)}（全账号汇总）</span>
                                                            </div>
                                                        </div>
                                                        <div className="exchange-venue-echarts-wrap">
                                                            <ReactECharts
                                                                option={getEquityOption(
                                                                    row.trends.map((t) => dayjs(t.equity_date).format("MM-DD")),
                                                                    row.trends.map((t) => t.net_assets),
                                                                    row.trends.map((t) => t.pnl),
                                                                )}
                                                                style={{ height: "280px", width: "100%" }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <Empty description="暂无交易所资金汇总数据" style={{ marginTop: 48 }} />
                                        ),
                                    },
                                ]} />
                            </div>
                        )
                    },
                    ...(canViewHedge
                        ? [
                              {
                                  key: "hedge",
                                  label: (
                                      <span
                                          style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 8,
                                              fontWeight: 600,
                                          }}
                                      >
                                          <Shield size={18} style={{ opacity: 0.9, verticalAlign: "middle" }} />
                                          对冲组合分析（资金视角）
                                          <Tooltip
                                              title="与本页权益走势、下方「交易量分析」对照：各组每日总交易量走势与区间合计（trade_volumes）、组合资金/净资产、区间盈亏与对冲效率；资金与盈亏来自权益快照。"
                                          >
                                              <InfoCircleOutlined
                                                  style={{
                                                      fontSize: 14,
                                                      opacity: 0.55,
                                                      cursor: "help",
                                                  }}
                                              />
                                          </Tooltip>
                                      </span>
                                  ),
                                  children: (
                                      <div className="charts-section">
                                          <HedgePanoramaSection
                                              dateFrom={dateRange[0].format("YYYY-MM-DD")}
                                              dateTo={dateRange[1].format("YYYY-MM-DD")}
                                              ownerUserId={ownerUserId}
                                          />
                                      </div>
                                  ),
                              },
                          ]
                        : []),
                    {
                        key: 'volume',
                        label: (
                            <span>
                                <BarChart3 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                <strong>交易量分析</strong>
                            </span>
                        ),
                        children: (
                            <div className="charts-section">
                                <Card className="main-chart-card">
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                        {tradeSummary && (
                                            <Tag color={tradeSummary.trend >= 0 ? "success" : "error"}>
                                                {tradeSummary.trend >= 0 ? <RiseOutlined /> : <FallOutlined />} {Math.abs(tradeSummary.trend || 0).toFixed(1)}% 环比
                                            </Tag>
                                        )}
                                    </div>
                                    {tradeDailyMeta.zeroDays > 0 && (
                                        <Alert
                                            showIcon
                                            type={tradeDailyMeta.activeDays > 0 ? "warning" : "info"}
                                            style={{ marginBottom: 16 }}
                                            message={tradeDailyMeta.activeDays > 0
                                                ? `当前区间有 ${tradeDailyMeta.zeroDays} 天无交易记录，走势图已按 0 补齐。`
                                                : "当前区间内暂无交易记录，走势图已按 0 补齐。"}
                                            description={tradeDailyMeta.activeDays > 0
                                                ? `本次共展示 ${tradeDailyMeta.totalDays} 天数据，其中 ${tradeDailyMeta.activeDays} 天存在成交。若与预期不符，请检查最近交易数据采集是否中断。`
                                                : "后端返回的日期已补齐，但全部日期均无成交记录；请先检查交易量采集任务、上报链路或筛选范围。"}
                                        />
                                    )}
                                    <div style={{ height: "350px" }}>
                                        <Line data={tradeDaily} options={volumeOptions} />
                                    </div>
                                </Card>
                                <Collapse ghost className="sub-collapse" items={[
                                    {
                                        key: 'sub-exchange-vol',
                                        label: `各交易所交易量趋势 (${tradeExchangeVenueGroups.length})`,
                                        children: tradeExchangeVenueGroups.length > 0 ? (
                                            <div className="exchange-venue-stack">
                                                {tradeExchangeVenueGroups.map((eg) => (
                                                    <React.Fragment key={eg.key}>
                                                        {eg.spot.hasData && (
                                                            <div className="exchange-venue-chart-row">
                                                                <div className="exchange-venue-row-head">
                                                                    <div className="exchange-venue-row-head-main">
                                                                        <span className="exchange-venue-row-title">{formatExchangeName(eg.exchange)}</span>
                                                                        <span className="exchange-venue-row-kind">现货（全市场汇总）</span>
                                                                    </div>
                                                                    <span className="exchange-venue-row-total">
                                                                        周期 T: {formatVolume(eg.periodVolume)}
                                                                    </span>
                                                                </div>
                                                                <div className="exchange-venue-chart-canvas">
                                                                    <Line data={eg.spot.chartData} options={pairDualAxisOptions} />
                                                                </div>
                                                            </div>
                                                        )}
                                                        {eg.swap.hasData && (
                                                            <div className="exchange-venue-chart-row">
                                                                <div className="exchange-venue-row-head">
                                                                    <div className="exchange-venue-row-head-main">
                                                                        <span className="exchange-venue-row-title">{formatExchangeName(eg.exchange)}</span>
                                                                        <span className="exchange-venue-row-kind">合约（全市场汇总）</span>
                                                                    </div>
                                                                    {!eg.spot.hasData && (
                                                                        <span className="exchange-venue-row-total">
                                                                            周期 T: {formatVolume(eg.periodVolume)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="exchange-venue-chart-canvas">
                                                                    <Line data={eg.swap.chartData} options={pairDualAxisOptions} />
                                                                </div>
                                                            </div>
                                                        )}
                                                        {!eg.spot.hasData && !eg.swap.hasData && (
                                                            <div className="exchange-venue-chart-row">
                                                                <div className="exchange-venue-row-head">
                                                                    <span className="exchange-venue-row-title">{formatExchangeName(eg.exchange)}</span>
                                                                </div>
                                                                <Empty description="该交易所无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ) : (
                                            <Empty description="暂无交易所趋势数据" style={{ marginTop: 48 }} />
                                        ),
                                    },
                                    {
                                        key: 'sub-acc',
                                        label: `分账号交易量 (${tradeAccountPairGroups.length}，现货/合约分开)`,
                                        children: (
                                            <div className="sub-grid">
                                                {tradeAccountPairGroups.slice(0, 12).flatMap((g) => {
                                                    const cards = [];
                                                    if (g.spot.hasData) {
                                                        cards.push(
                                                            <Card
                                                                key={`${g.key}-spot`}
                                                                title={`${g.account} (${formatExchangeName(g.exchange)}) · 现货`}
                                                                size="small"
                                                                className="sub-chart-card"
                                                                extra={<span>T: {formatVolume(g.spot.periodVolume)}</span>}
                                                            >
                                                                <div style={{ height: 220 }}>
                                                                    <Line data={g.spot.chartData} options={accountPairMultiLineOptions} />
                                                                </div>
                                                            </Card>,
                                                        );
                                                    }
                                                    if (g.swap.hasData) {
                                                        cards.push(
                                                            <Card
                                                                key={`${g.key}-swap`}
                                                                title={`${g.account} (${formatExchangeName(g.exchange)}) · 合约`}
                                                                size="small"
                                                                className="sub-chart-card"
                                                                extra={<span>T: {formatVolume(g.swap.periodVolume)}</span>}
                                                            >
                                                                <div style={{ height: 220 }}>
                                                                    <Line data={g.swap.chartData} options={accountPairMultiLineOptions} />
                                                                </div>
                                                            </Card>,
                                                        );
                                                    }
                                                    return cards;
                                                })}
                                            </div>
                                        ),
                                    },
                                    {
                                        key: 'sub-pair',
                                        label: `分交易对交易量 (${tradePairSplitGroups.length}，现货/合约分开)`,
                                        children: (
                                            <div className="sub-grid">
                                                {tradePairSplitGroups.slice(0, 12).flatMap((pg) => {
                                                    const cards = [];
                                                    if (pg.spot.hasData) {
                                                        cards.push(
                                                            <Card
                                                                key={`${pg.key}-spot`}
                                                                title={`${pg.pair} · 现货`}
                                                                size="small"
                                                                className="sub-chart-card"
                                                                extra={<span>{formatVolume(pg.spot.periodVolume)}</span>}
                                                            >
                                                                <div style={{ height: 200 }}>
                                                                    <Line data={pg.spot.chartData} options={pairDualAxisOptions} />
                                                                </div>
                                                            </Card>,
                                                        );
                                                    }
                                                    if (pg.swap.hasData) {
                                                        cards.push(
                                                            <Card
                                                                key={`${pg.key}-swap`}
                                                                title={`${pg.pair} · 合约`}
                                                                size="small"
                                                                className="sub-chart-card"
                                                                extra={<span>{formatVolume(pg.swap.periodVolume)}</span>}
                                                            >
                                                                <div style={{ height: 200 }}>
                                                                    <Line data={pg.swap.chartData} options={pairDualAxisOptions} />
                                                                </div>
                                                            </Card>,
                                                        );
                                                    }
                                                    return cards;
                                                })}
                                            </div>
                                        ),
                                    },
                                ]} />
                            </div>
                        )
                    }
                ]} />
            </Spin>
        </div>
    );
}

function CompositeOverview() {
    const panoramaCaptureRef = useRef(null);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(7, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
    ]);
    const [activeQuickDate, setActiveQuickDate] = useState("近7天");
    const [adminPanoramaKey, setAdminPanoramaKey] = useState(["all"]);
    const { isAdmin, adminUsers, loadingUsers } = useAdminPanoramaUsers();

    const exportOwnerUserId = useMemo(
        () => resolvePanoramaExportOwnerUserId(isAdmin, adminPanoramaKey),
        [isAdmin, adminPanoramaKey],
    );

    const dateFromStr = dateRange[0]?.format("YYYY-MM-DD");
    const dateToStr = dateRange[1]?.format("YYYY-MM-DD");

    const handleExportExcel = useCallback(async () => {
        if (!dateFromStr || !dateToStr) return;
        const hide = message.loading("正在导出 Excel...", 0);
        try {
            await exportCompositeOverviewExcel({
                dateFrom: dateFromStr,
                dateTo: dateToStr,
                ownerUserId: exportOwnerUserId,
            });
            message.success("Excel 已下载");
        } catch (e) {
            console.error(e);
            message.error(e.apiMessage || e.message || "导出失败");
        } finally {
            hide();
        }
    }, [dateFromStr, dateToStr, exportOwnerUserId]);

    const handleExportPdf = useCallback(async () => {
        if (!dateFromStr || !dateToStr) return;
        const hide = message.loading("正在展开图表并生成 PDF...", 0);
        try {
            await capturePanoramaToPdf(
                panoramaCaptureRef.current,
                `综合分析全景_${dateFromStr}_${dateToStr}`,
            );
            message.success("PDF 已下载");
        } catch (e) {
            console.error(e);
            message.error(e.message || "PDF 导出失败");
        } finally {
            hide();
        }
    }, [dateFromStr, dateToStr]);

    const handleQuickDate = (label) => {
        const option = QUICK_DATES.find((d) => d.label === label);
        if (option) {
            setDateRange(option.value());
            setActiveQuickDate(label);
        }
    };

    const headerBar = (
        <div className="overview-header">
            <div className="header-left">
                <Title level={3} style={{ margin: 0 }}>
                    综合分析全景
                </Title>
                <Text type="secondary">
                    盈亏走势与交易量数据一站式监测；对冲组合含各组区间总交易量与期末组合资金，可与下方「交易量分析」对照。
                    {isAdmin ? "管理员可展开查看各普通用户名下看板明细。" : ""}
                </Text>
            </div>
            <Space size="middle" className="header-controls">
                <div className="quick-date-buttons">
                    {QUICK_DATES.map((item) => (
                        <button
                            key={item.label}
                            type="button"
                            className={`quick-date-btn ${activeQuickDate === item.label ? "active" : ""}`}
                            onClick={() => handleQuickDate(item.label)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                        if (dates) {
                            setDateRange(dates);
                            setActiveQuickDate("");
                        }
                    }}
                    allowClear={false}
                />
                <PanoramaExportButtons onExportExcel={handleExportExcel} onExportPdf={handleExportPdf} />
            </Space>
        </div>
    );

    if (!isAdmin) {
        return (
            <div className="composite-overview-container" ref={panoramaCaptureRef}>
                {headerBar}
                <CompositeOverviewBody ownerUserId={null} dateRange={dateRange} />
            </div>
        );
    }

    return (
        <div className="composite-overview-container" ref={panoramaCaptureRef}>
            {headerBar}
            <Spin spinning={loadingUsers}>
                <Collapse
                    destroyOnHidden
                    activeKey={adminPanoramaKey}
                    onChange={(keys) => setAdminPanoramaKey(Array.isArray(keys) ? keys : [keys])}
                    className="admin-user-panorama-collapse"
                    items={[
                        {
                            key: "all",
                            label: "全平台汇总（全部用户数据）",
                            children: <CompositeOverviewBody ownerUserId={null} dateRange={dateRange} />,
                        },
                        ...adminUsers
                            .filter((u) => u.role === "user")
                            .map((u) => ({
                                key: String(u.id),
                                label: `${u.username}（ID ${u.id}）${u.email ? ` · ${u.email}` : ""}`,
                                children: <CompositeOverviewBody ownerUserId={u.id} dateRange={dateRange} />,
                            })),
                    ]}
                />
            </Spin>
        </div>
    );
}

export default CompositeOverview;
